let credential = '';
let selection = '';
let credentialVersion = 0;

export function setPlatformCredential(value: string) {
  credential = value.trim(); selection = ''; credentialVersion++;
}
export function setTenantSelection(value: string) { selection = value; }

export async function platformLogin(username: string, password: string): Promise<{ expiresAt: string }> {
  const response = await fetch('/api/v1/auth/login', {
    method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
    signal: AbortSignal.timeout(15_000),
  });
  if (response.status === 401) throw new Error('Invalid platform administrator credentials.');
  if (!response.ok) throw new Error('Platform management is unavailable. Start the control-plane service and retry.');
  const result = await response.json() as { token?: string; expiresAt?: string };
  if (typeof result.token !== 'string' || typeof result.expiresAt !== 'string') throw new Error('Platform login returned an invalid response.');
  setPlatformCredential(result.token);
  return { expiresAt: result.expiresAt };
}

export async function platformRequest<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  if (!/^\/[a-zA-Z0-9/_?=&%.,-]*$/.test(path) || path.startsWith('//')) throw new Error('Invalid platform path.');
  if (!credential) throw new Error('Connect with an operator or tenant access credential.');
  const version = credentialVersion;
  const response = await fetch(`/api/v1${path}`, {
    method, credentials: 'omit', cache: 'no-store', redirect: 'error',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${credential}`,
      ...(selection ? { 'X-AwareNow-Tenant-Selection': selection } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: AbortSignal.timeout(45_000),
  });
  if (version !== credentialVersion) throw new Error('Access changed. Reconnect to refresh this view.');
  if (response.status === 401) { setPlatformCredential(''); throw new Error('Access expired or was revoked. Reconnect with a valid credential.'); }
  if (response.status === 403) throw new Error('This credential does not have permission for this action.');
  if (response.status === 429) throw new Error('Request limit reached. Wait a moment and retry.');
  if (response.status === 409) throw new Error('The selected configuration is not ready. Check credentials, models and tenant status.');
  if (response.status === 400) throw new Error('Check the entered values and try again.');
  if (!response.ok) throw new Error('The service is unavailable. Check its connection and configuration, then retry.');
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}

export type Provider = 'commandcode' | 'deepseek';
export type LicenseStatus = 'ACTIVE' | 'SUSPENDED' | 'REVOKED' | 'EXPIRED';
export interface LicenseView {
  id: string; tenantId: string | null; plan: string; seatLimit: number;
  startsAt: string; expiresAt: string | null; status: LicenseStatus; createdAt: string; updatedAt: string;
}
export const licenseApi = {
  list: () => platformRequest<{ licenses: LicenseView[] }>('/licenses'),
  create: (input: { tenantId: string | null; plan: string; seatLimit: number; startsAt: string; expiresAt: string | null }) => platformRequest<LicenseView>('/licenses', 'POST', input),
  update: (id: string, input: { tenantId: string | null; plan: string; seatLimit: number; startsAt: string; expiresAt: string | null; status: LicenseStatus }) => platformRequest<LicenseView>(`/licenses/${id}`, 'PATCH', input),
  revoke: (id: string) => platformRequest<void>(`/licenses/${id}/revoke`, 'POST'),
};
export interface AiSettings {
  provider: Provider; modelId: string | null; allowedModelIds: string[]; freeOnly: boolean;
  updatedAt: string | null; generationAvailable: boolean; generationUnavailableReason?: string;
}
export interface AiModel {
  id: string; displayName: string; capability: string; available: boolean;
  pricingStatus: 'free' | 'paid' | 'unknown'; pricingSource: string;
  pricingVerifiedAt: string; pricingExpiresAt: string; pricingFresh: boolean;
  entitlementStatus: string; eligible: boolean;
}
export interface AiCatalog { provider: Provider; refreshedAt: string | null; expiresAt: string | null; catalogFresh: boolean; models: AiModel[] }
export const aiApi = {
  settings: () => platformRequest<AiSettings>('/platform/ai/settings'),
  // There is no separate "enabled" toggle server-side: readiness is fully
  // derived from provider/model/catalog state (see generationAvailable).
  save: (settings: Pick<AiSettings, 'provider' | 'modelId' | 'allowedModelIds' | 'freeOnly'>) => platformRequest<AiSettings>('/platform/ai/settings', 'PUT', settings),
  catalog: (provider: Provider, refresh = false) => platformRequest<AiCatalog>(`/platform/ai/providers/${provider}/models${refresh ? '/refresh' : ''}`, refresh ? 'POST' : 'GET'),
  credential: (provider: Provider) => platformRequest<{ configured: boolean; updatedAt: string | null }>(`/platform/ai/providers/${provider}/credential`),
  replaceKey: (provider: Provider, apiKey: string) => platformRequest(`/platform/ai/providers/${provider}/credential`, 'PUT', { apiKey }),
  revokeKey: (provider: Provider) => platformRequest(`/platform/ai/providers/${provider}/credential`, 'DELETE'),
  test: (provider: Provider) => platformRequest<{ credentialStatus?: string; status?: string; modelCount?: number }>(`/platform/ai/providers/${provider}/test-connection`, 'POST'),
  usage: () => platformRequest<Record<string, unknown>>('/platform/ai/usage'),
  generate: (body: unknown) => platformRequest<{ kind: string; provider: Provider; modelId: string; result: unknown; reviewRequired: boolean; authoritative: boolean; usage: { promptTokens: number; completionTokens: number; totalTokens: number } }>('/platform/ai/generate', 'POST', body),
};

export interface AuditEntry {
  id: string;
  tenantId: string;
  actorSubjectId: string;
  action: string;
  entityType: string;
  entityId: string;
  occurredAt: string;
  metadata: Record<string, unknown>;
}

export const auditApi = {
  list: () => platformRequest<{ entries: AuditEntry[] }>('/audit'),
};
